import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Table, Button, Modal, Input, Form, Space, Tag, Avatar } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, AppstoreOutlined } from '@ant-design/icons';
import BASEURL from '../API/Config';

const BrandMaster = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editBrandId, setEditBrandId] = useState(null);
  const [existingImages, setExistingImages] = useState([]);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [newImageInputs, setNewImageInputs] = useState([{ id: Date.now(), file: null }]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const token = localStorage.getItem('token');

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASEURL}/master/getbrand`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setBrands(data);
    } catch {
      Swal.fire('Error', 'Could not load brands.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBrands(); }, []);

  const openAddModal = () => {
    setIsEditing(false);
    setExistingImages([]);
    setRemovedImageIds([]);
    setNewImageInputs([{ id: Date.now(), file: null }]);
    setEditBrandId(null);
    setShowModal(true);
  };

  const handleEdit = (brand) => {
    setIsEditing(true);
    setEditBrandId(brand._id);
    setExistingImages(brand.images || []);
    setRemovedImageIds([]);
    setNewImageInputs([{ id: Date.now(), file: null }]);
    setShowModal(true);
  };

  const handleImageFileChange = (id, file) => {
    setNewImageInputs((prev) => prev.map((inp) => inp.id === id ? { ...inp, file } : inp));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditing && newImageInputs.every((i) => !i.file)) {
      Swal.fire('Error', 'Select at least one image.', 'error');
      return;
    }
    setIsSubmitting(true);
    const formData = new FormData();
    removedImageIds.forEach((id) => formData.append('removeImages', id));
    newImageInputs.forEach((inp) => { if (inp.file) formData.append('images', inp.file); });
    try {
      const url = isEditing ? `${BASEURL}/master/updatebrand/${editBrandId}` : `${BASEURL}/master/createbrand`;
      const res = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error();
      Swal.fire('Success', isEditing ? 'Brand updated!' : 'Brand added!', 'success');
      setShowModal(false);
      fetchBrands();
    } catch {
      Swal.fire('Error', 'Failed to process request.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Brand?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e53935',
      confirmButtonText: 'Yes, Delete',
    });
    if (!result.isConfirmed) return;
    await fetch(`${BASEURL}/master/deletebrand/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    Swal.fire('Deleted!', 'Brand removed.', 'success');
    fetchBrands();
  };

  const filtered = brands.filter(b => (b.brand || '').toLowerCase().includes(search.toLowerCase()));

  const columns = [
    {
      title: "S.No",
      key: "index",
      width: 60,
      render: (_, __, i) => <span style={{ color: "#6b7280" }}>{i + 1}</span>,
    },
    {
      title: 'BRAND',
      key: 'brand',
      render: (r) => (
        <Space>
          <Avatar shape="square" size={36} icon={<AppstoreOutlined />}
            style={{ background: '#e8f1ff', color: '#2874f0', borderRadius: 8 }} />
          <span style={{ fontWeight: 600, color: '#212121' }}>{r.brand || '—'}</span>
        </Space>
      ),
    },
    {
      title: 'IMAGES',
      key: 'images',
      render: (r) => (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {r.images?.length ? r.images.map((img) => (
            <img key={img._id} src={`${BASEURL}${img.url}`} alt="brand"
              style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid #e0e0e0' }} />
          )) : <span style={{ color: '#9ca3af', fontSize: 13 }}>No Images</span>}
        </div>
      ),
    },
    {
      title: 'STATUS',
      key: 'status',
      render: () => <Tag color="green" style={{ borderRadius: 50, fontWeight: 600 }}>Active</Tag>,
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 120,
      render: (r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(r)}
            style={{ color: '#2874f0', borderColor: '#2874f0', borderRadius: 4 }} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r._id)}
            style={{ borderRadius: 4 }} />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>Masters</div>
          <h2 style={{ margin: 0, fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 700, color: '#212121' }}>Brand Master</h2>
        </div>
        {brands.length === 0 && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}
            style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, height: 38, fontWeight: 600 }}>
            Add Brand
          </Button>
        )}
      </div>

      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8ecf0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Input prefix={<SearchOutlined style={{ color: '#9ca3af' }} />} placeholder="Search brand..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            style={{ width: 240, borderRadius: 4 }} />
          <div style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>
            Total: <strong style={{ color: '#212121' }}>{filtered.length}</strong>
          </div>
        </div>
        <Table columns={columns} dataSource={filtered} rowKey="_id" loading={loading}
          pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "30", "40"], showTotal: (t) => `${t} brands` }} size="middle" />
      </div>

      {/* Add/Edit Modal */}
      <Modal
        title={<span style={{ fontFamily: 'Outfit,sans-serif', fontWeight: 700 }}>{isEditing ? 'Edit Brand' : 'Add Brand'}</span>}
        open={showModal} onCancel={() => setShowModal(false)} footer={null} centered width={500}
      >
        <form onSubmit={handleSubmit} style={{ marginTop: 16 }}>
          {isEditing && existingImages.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Existing Images</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {existingImages.map((img) => (
                  <div key={img._id} style={{ position: 'relative' }}>
                    <img src={`${BASEURL}${img.url}`} alt="" style={{ width: 70, height: 60, objectFit: 'cover', borderRadius: 6, border: '1px solid #e0e0e0' }} />
                    <button type="button" onClick={() => {
                      setRemovedImageIds(p => [...p, img._id]);
                      setExistingImages(p => p.filter(i => i._id !== img._id));
                    }} style={{ position: 'absolute', top: -6, right: -6, background: '#e53935', border: 'none', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, cursor: 'pointer' }}>×</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Upload Images</div>
          {newImageInputs.map((inp) => (
            <div key={inp.id} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <input type="file" accept="image/*" onChange={(e) => handleImageFileChange(inp.id, e.target.files[0])}
                style={{ flex: 1, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }} />
              {newImageInputs.length > 1 && (
                <button type="button" onClick={() => setNewImageInputs(p => p.filter(i => i.id !== inp.id))}
                  style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, padding: '6px 10px', color: '#e53935', cursor: 'pointer', fontSize: 13 }}>✕</button>
              )}
            </div>
          ))}
          <button type="button" onClick={() => setNewImageInputs(p => [...p, { id: Date.now(), file: null }])}
            style={{ background: '#e8f1ff', border: '1px solid #2874f0', borderRadius: 4, padding: '6px 14px', color: '#2874f0', cursor: 'pointer', fontSize: 13, fontWeight: 500, marginBottom: 20 }}>
            + Add More
          </button>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={isSubmitting}
              style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, fontWeight: 600 }}>
              {isEditing ? 'Update Brand' : 'Add Brand'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default BrandMaster;

